import { type RefObject, useEffect, useState } from "react";

interface UseInViewOptions {
  rootMargin?: string;
  threshold?: number;
}

export function useInView(
  ref: RefObject<Element | null>,
  { rootMargin = "0px", threshold = 0 }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;

    // Synchronous check for elements already in viewport — the
    // IntersectionObserver callback is async and can miss elements
    // that are visible at the time observe() is called.
    const rect = el.getBoundingClientRect();
    const margin = Number.parseInt(rootMargin) || 0;
    if (
      rect.top < window.innerHeight + margin &&
      rect.bottom > -margin &&
      rect.left < window.innerWidth + margin &&
      rect.right > -margin
    ) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin, threshold, inView]);

  return inView;
}
